import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/data/api";

// Per-repo preview configs (Settings > Previews, plan 032 self-serve). The
// customer installs the GitHub App, then describes how to build a preview for
// each repo. The control plane reads these by repo at PR time.

export interface PreviewConfigRow {
  id: string;
  repo: string;
  // The App has fired at least one webhook for this repo (installation known).
  connected: boolean;
  base_image: string;
  boot_command: string;
  ready_pattern: string;
  fail_pattern: string | null;
  health_process: string | null;
  patch_overlay: string | null;
  env: Record<string, string>;
  enabled: boolean;
  created_at: string | number;
  updated_at: string | number;
}

export interface PreviewConfigInput {
  base_image: string;
  boot_command: string;
  ready_pattern: string;
  fail_pattern: string | null;
  health_process: string | null;
  patch_overlay: string | null;
  env: Record<string, string>;
  enabled: boolean;
}

export interface PreviewAppInfo {
  configured: boolean;
  slug: string;
  install_url: string;
}

interface PreviewConfigsResponse {
  configs: PreviewConfigRow[];
  app: PreviewAppInfo;
}

const previewKeys = {
  list: (workspaceSlug: string | null | undefined) =>
    ["preview-configs", workspaceSlug ?? null] as const,
};

export function usePreviewConfigsQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: previewKeys.list(workspaceSlug),
    queryFn: () => listConfigs(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function useCreatePreviewConfigMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PreviewConfigInput & { repo: string }) =>
      createConfig(workspaceSlug ?? "", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: previewKeys.list(workspaceSlug),
      });
    },
  });
}

export function useUpdatePreviewConfigMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; config: PreviewConfigInput }) =>
      updateConfig(workspaceSlug ?? "", input.id, input.config),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: previewKeys.list(workspaceSlug),
      });
    },
  });
}

export function useDeletePreviewConfigMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConfig(workspaceSlug ?? "", id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: previewKeys.list(workspaceSlug),
      });
    },
  });
}

function listConfigs(workspaceSlug: string) {
  return apiJson<PreviewConfigsResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/preview-configs`,
  );
}

function createConfig(
  workspaceSlug: string,
  input: PreviewConfigInput & { repo: string },
) {
  return apiJson<{ config: PreviewConfigRow }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/preview-configs`,
    { method: "POST", json: input },
  );
}

function updateConfig(
  workspaceSlug: string,
  id: string,
  config: PreviewConfigInput,
) {
  return apiJson<{ config: PreviewConfigRow }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/preview-configs/${encodeURIComponent(id)}`,
    { method: "PUT", json: config },
  );
}

function deleteConfig(workspaceSlug: string, id: string) {
  return apiJson<{ ok: true }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/preview-configs/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

// --- env <-> KEY=value text helpers (used by the editor) --------------------

export function envToText(env: Record<string, string>): string {
  return Object.entries(env)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}

export function parseEnvText(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const eq = trimmed.indexOf("=");
    if (eq <= 0) continue;
    const key = trimmed.slice(0, eq).trim();
    const value = trimmed.slice(eq + 1).trim();
    if (key) out[key] = value;
  }
  return out;
}
